import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { ASSETS } from "../../lib/assets";

const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

const lineVariants = {
  hidden: { y: "110%" },
  visible: (i) => ({
    y: "0%",
    transition: { duration: 1, delay: 0.35 + i * 0.14, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const Hero = () => {
  const { t } = useLang();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  const lines = [t.hero.line1, t.hero.line2, t.hero.line3];

  return (
    <section id="hero" ref={ref} className="relative min-h-[100svh] w-full overflow-hidden bg-brand-steel" data-testid="hero">
      {/* Background image with parallax */}
      <motion.div className="absolute inset-0" style={{ y: imgY, scale: imgScale }}>
        <picture>
          <source media="(max-width: 767px)" srcSet={ASSETS.heroMobile} />
          <img
            src={ASSETS.heroDesktop}
            alt="Sabbiatura di un serbatoio industriale in raffineria"
            className="h-full w-full object-cover"
            fetchPriority="high"
          />
        </picture>
      </motion.div>

      {/* Gradient overlays */}
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-r from-brand-steel via-brand-steel/70 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-steel via-transparent to-brand-steel/40" />
      <div className="absolute inset-0 blueprint-grid opacity-40" />

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 min-h-[100svh] flex flex-col justify-center pt-24 pb-28">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          className="font-condensed text-sm uppercase tracking-[0.3em] text-brand-azure mb-8"
          data-testid="hero-eyebrow"
        >
          {t.hero.eyebrow}
        </motion.div>

        <h1 className="font-display font-semibold text-white leading-[0.95] text-5xl sm:text-6xl lg:text-8xl max-w-4xl">
          {lines.map((line, i) => (
            <span key={i} className="line-mask">
              <motion.span className="block" custom={i} variants={lineVariants} initial="hidden" animate="visible">
                {i === 1 ? <span className="text-brand-azure">{line}</span> : line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="mt-8 max-w-xl text-base sm:text-lg text-slate-300 leading-relaxed"
          data-testid="hero-sub"
        >
          {t.hero.sub}
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1.25, ease: [0.16, 1, 0.3, 1] }}
          className="mt-10 flex flex-col sm:flex-row gap-4"
        >
          <button
            onClick={() => scrollTo("contact")}
            className="group inline-flex items-center justify-center gap-2 font-condensed uppercase tracking-[0.15em] font-semibold text-brand-navy bg-brand-azure hover:bg-white transition-colors rounded-full px-7 py-4"
            data-testid="hero-cta-primary"
          >
            {t.hero.ctaPrimary}
            <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
          </button>
          <button
            onClick={() => scrollTo("services")}
            className="inline-flex items-center justify-center gap-2 font-condensed uppercase tracking-[0.15em] font-semibold text-white border border-white/25 hover:border-white hover:bg-white/5 transition-colors rounded-full px-7 py-4"
            data-testid="hero-cta-secondary"
          >
            {t.hero.ctaSecondary}
          </button>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={() => scrollTo("trust")}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 text-slate-400 hover:text-white transition-colors"
        data-testid="hero-scroll"
      >
        <span className="font-condensed text-xs uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        <motion.span animate={{ y: [0, 8, 0] }} transition={{ duration: 1.6, repeat: Infinity }}>
          <ArrowDown size={18} />
        </motion.span>
      </motion.button>
    </section>
  );
};
